import { useState, useEffect, useRef } from 'react'
import { Loader, SlidersHorizontal, Edit2, GripVertical, Plus, Trash2, MoreVertical } from 'lucide-react'
import { alarmApi } from '../lib/alarmApi'
import { EventMonitorSettingsModal } from './EventMonitorSettingsModal'

const FILTERS = ['Enabled', 'All', 'Triggered', 'Acknowledged', 'Disabled']

const statusOf = (a) => {
  if (a.triggered) return { label: 'Triggered', cls: 'text-red-600' }
  if (a.acknowledged_time || a.acknowledged_by) return { label: 'Acknowledged', cls: 'text-amber-600' }
  if (a.enabled) return { label: 'Normal', cls: 'text-green-600' }
  return { label: 'Disabled', cls: 'text-gray-400' }
}

const eventTime = (a) => {
  const t = a.acknowledged_time || a.last_checked || a.last_no_match_trigger_time
  if (!t) return '—'
  try { return new Date(t).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) }
  catch { return String(t) }
}

/**
 * Event Monitor / Alarm Summary panel with an inline edit mode matching the XD:
 *  - View: title + Filter + edit pencil; table (#, Name, Type, Status)
 *  - Edit: Save Events / Cancel; drag-reorder rows; per-row 3-dot (Delete);
 *          "+ Add Event Monitor"; "Drag Event" delete drop-zone; Event Time column
 */
export function EventMonitorPanel({ alarms = [], loading = false, onChanged, sites = [], projectId }) {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [editingAlarm, setEditingAlarm] = useState(null)
  const [filter, setFilter] = useState('All')
  const [showFilter, setShowFilter] = useState(false)
  const filterRef = useRef(null)

  const [editing, setEditing] = useState(false)
  const [working, setWorking] = useState([])        // editable copy (ordered)
  const [pendingDeletes, setPendingDeletes] = useState([])
  const [saving, setSaving] = useState(false)
  const [rowMenu, setRowMenu] = useState(null)      // id of row whose menu is open (used in BOTH view + edit mode)
  const [actionBusy, setActionBusy] = useState(null) // id of row whose ack/pause action is in flight
  const [dragIndex, setDragIndex] = useState(null)
  const [dropArmed, setDropArmed] = useState(false) // delete drop-zone hover

  // Close any open row menu when clicking outside the table — without this
  // the menu stays open after a click anywhere else on the dashboard,
  // covering content beneath it.
  const rowsContainerRef = useRef(null)
  useEffect(() => {
    if (rowMenu === null) return
    const onClick = (e) => {
      if (rowsContainerRef.current && !rowsContainerRef.current.contains(e.target)) {
        setRowMenu(null)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [rowMenu])

  // ── Row actions: Acknowledge / Pause / Resume ─────────────────────────
  const runRowAction = async (alarm, fn) => {
    if (!alarm?.id) return
    setActionBusy(alarm.id)
    setRowMenu(null)
    try {
      await fn(alarm.id)
      onChanged?.()
    } catch (e) {
      console.error('[EventMonitor] row action failed', alarm.id, e)
    } finally {
      setActionBusy(null)
    }
  }
  const acknowledgeRow = (a) => runRowAction(a, alarmApi.acknowledgeAlarm)
  const togglePauseRow = (a) => runRowAction(a, a.enabled ? alarmApi.pauseAlarm : alarmApi.resumeAlarm)

  useEffect(() => {
    if (!showFilter) return
    const close = (e) => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showFilter])

  // While in edit mode, if the alarms list refreshes (e.g. a new event was just
  // created/saved via the modal), re-sync the editable copy so it appears
  // immediately — without reloading the page. Honors staged deletions.
  useEffect(() => {
    if (editing) {
      setWorking(alarms.filter(a => !pendingDeletes.includes(a.id)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarms])

  const enterEdit = () => {
    setWorking(alarms.slice())
    setPendingDeletes([])
    setRowMenu(null)
    setEditing(true)
  }
  const cancelEdit = () => {
    setEditing(false); setWorking([]); setPendingDeletes([]); setRowMenu(null)
  }
  const saveEdit = async () => {
    setSaving(true)
    try {
      for (const id of pendingDeletes) {
        try { await alarmApi.deleteAlarm(id) } catch (e) { console.error('[EventMonitor] delete failed', id, e) }
      }
      setEditing(false); setPendingDeletes([]); setRowMenu(null)
      onChanged?.()
    } finally { setSaving(false) }
  }

  const stageDelete = (id) => {
    setPendingDeletes(prev => prev.includes(id) ? prev : [...prev, id])
    setWorking(prev => prev.filter(a => a.id !== id))
    setRowMenu(null)
  }

  // ── drag-to-reorder ──
  const onRowDragStart = (idx) => setDragIndex(idx)
  const onRowDragOver = (e, idx) => {
    e.preventDefault()
    if (dragIndex === null || dragIndex === idx) return
    setWorking(prev => {
      const next = prev.slice()
      const [moved] = next.splice(dragIndex, 1)
      next.splice(idx, 0, moved)
      return next
    })
    setDragIndex(idx)
  }
  const onRowDragEnd = () => setDragIndex(null)

  // ── drag-to-delete drop zone ──
  const onDeleteZoneDrop = (e) => {
    e.preventDefault()
    setDropArmed(false)
    if (dragIndex !== null && working[dragIndex]) stageDelete(working[dragIndex].id)
    setDragIndex(null)
  }

  // Single source of truth for the status filter, shared by the list and the
  // per-option counts so they can never drift apart.
  const matchesFilter = (a, f) => {
    switch (f) {
      case 'Enabled':      return !!a.enabled
      case 'Triggered':    return !!a.triggered
      case 'Acknowledged': return !!(a.acknowledged_time || a.acknowledged_by)
      case 'Disabled':     return !a.enabled
      default:             return true   // 'All'
    }
  }
  const counts = FILTERS.reduce((m, f) => { m[f] = alarms.filter(a => matchesFilter(a, f)).length; return m }, {})
  const rows = editing ? working : alarms.filter(a => matchesFilter(a, filter))

  return (
    <div className="bg-white border-[#358ECD]/30 rounded-lg p-4">
      {/* Header */}
      <div className="envir-field flex items-center justify-between mb-3">
        <h3 className="common-head18 font-bold text-[#4D4D4D] text-base">Event Monitor</h3>
        <div className="evnt-moni flex items-center gap-2">
          {/* Filter (always available) */}
          <div className="relative" ref={filterRef}>
            <button type="button" onClick={() => setShowFilter(v => !v)}
              className="common-size18 flex items-center gap-1.5 px-3 py-2 bg-[#F1F2F4] border border-gray-300 rounded-md text-xs text-[#4D4D4D] hover:bg-gray-50 transition">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Filter
            </button>
            {showFilter && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-2xl z-[9999] py-2">
                {FILTERS.map(f => (
                  <label key={f} className="flex items-center justify-between gap-2.5 px-4 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer">
                    <span className="flex items-center gap-2.5">
                      <input type="radio" name="evtFilter" checked={filter === f}
                        onChange={() => { setFilter(f); setShowFilter(false) }} className="accent-[#358ECD]" />
                      {f}
                    </span>
                    <span className="text-[11px] text-gray-400 tabular-nums">{counts[f]}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {editing ? (
            <>
              <button type="button" onClick={saveEdit} disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-[#358ECD] text-white text-xs font-semibold rounded-md hover:bg-[#2d7ab8] transition disabled:opacity-60">
                {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : null} Save Events
              </button>
              <button type="button" onClick={cancelEdit}
                className="px-4 py-1.5 border border-gray-300 text-gray-600 text-xs rounded-md hover:bg-gray-50 transition">
                Cancel
              </button>
            </>
          ) : (
            <button type="button" title="Edit events" onClick={enterEdit}
              className="p-1.5 text-gray-400 hover:text-[#358ECD] hover:bg-gray-100 rounded transition">
              <Edit2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Loader className="w-4 h-4 animate-spin" /> Loading events…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">
          No events{!editing && filter !== 'All' ? ` (${filter})` : ''} for this project.
        </p>
      ) : (
        <table className="w-full text-xs text-gray-700" ref={rowsContainerRef}>
          <thead className="border-b border-gray-200">
            <tr>
              {editing && <th className="w-6"></th>}
              <th className="text-left py-4 px-4 font-semibold text-[#4D4D4D] common-size15">#</th>
              <th className="text-left py-4 px-4 font-semibold text-[#4D4D4D] common-size15">Name</th>
              <th className="text-left py-4 px-4 font-semibold text-[#4D4D4D] common-size15">Type</th>
              <th className="text-left py-4 px-4 font-semibold text-[#4D4D4D] common-size15">Status</th>
              <th className="text-left py-4 px-4 font-semibold text-[#4D4D4D] common-size15">Event Time</th>
              {/* 3-dot column header — kept empty in both view + edit modes
                  so the kebab buttons have a consistent slot to anchor to. */}
              <th className="w-6"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a, i) => {
              const st = statusOf(a)
              const busy = actionBusy === a.id
              return (
                <tr key={a.id || i}
                  draggable={editing}
                  onDragStart={() => editing && onRowDragStart(i)}
                  onDragOver={(e) => editing && onRowDragOver(e, i)}
                  onDragEnd={onRowDragEnd}
                  className={`border-b border-gray-100 last:border-0 ${editing ? 'cursor-move hover:bg-gray-50' : ''} ${dragIndex === i ? 'bg-[#4D4D4D]' : ''}`}>
                  {editing && (
                    <td className="px-1 text-gray-300"><GripVertical className="w-4 h-4" /></td>
                  )}
                  <td className="py-4 px-4 text-[#4D4D4D] common-size15">{i + 1}</td>
                  <td className="py-4 px-4 text-[#4D4D4D] common-size15">{a.name || '—'}</td>
                  <td className="py-4 px-4 text-[#4D4D4D] common-size15 capitalize">{a.type || 'Event'}</td>
                  <td className="py-4 px-4 text-[#4D4D4D] common-size15"><span className={st.cls}>● {st.label}</span></td>
                  <td className="py-4 px-4 text-[#4D4D4D] common-size15">{eventTime(a)}</td>
                  {/* Row kebab — Edit/Delete in edit mode, Acknowledge/Pause
                      (or Resume) in view mode. Single column so the table
                      width is stable when toggling modes. */}
                  <td className="px-1 relative">
                    <button
                      type="button"
                      onClick={() => setRowMenu(rowMenu === a.id ? null : a.id)}
                      disabled={busy}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded disabled:opacity-50"
                      aria-label="Row actions"
                    >
                      {busy ? <Loader className="w-5 h-5 animate-spin" /> : <MoreVertical className="w-5 h-5" />}
                    </button>
                    {rowMenu === a.id && (
                      <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] py-1 text-[13px]">
                        {editing ? (
                          <>
                            <button className="common-size15 w-full text-left px-3 py-1.5 hover:bg-gray-50" onClick={() => { setRowMenu(null); setEditingAlarm(a); setSettingsOpen(true) }}>Edit</button>
                            <button className="common-size15 w-full text-left px-3 py-1.5 text-red-600 hover:bg-red-50" onClick={() => stageDelete(a.id)}>Delete</button>
                          </>
                        ) : (
                          <>
                            <button className="common-size15 w-full text-left px-3 py-1.5 hover:bg-gray-50" onClick={() => acknowledgeRow(a)}>Acknowledge</button>
                            <button className="w-full text-left common-size15 px-3 py-1.5 hover:bg-gray-50" onClick={() => togglePauseRow(a)}>
                              {a.enabled ? 'Pause' : 'Resume'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {/* Edit-mode footer */}
      {editing && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <button type="button" onClick={() => { setEditingAlarm(null); setSettingsOpen(true) }}
            className="flex items-center gap-1.5 text-sm text-[#358ECD] hover:underline">
            <Plus className="w-4 h-4" /> Add Event Monitor
          </button>
          <div
            onDragOver={(e) => { e.preventDefault(); setDropArmed(true) }}
            onDragLeave={() => setDropArmed(false)}
            onDrop={onDeleteZoneDrop}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dashed text-xs transition ${
              dropArmed ? 'border-red-400 bg-red-50 text-red-600' : 'border-gray-300 text-gray-400'
            }`}>
            <Trash2 className="w-4 h-4" /> Drag Event
          </div>
        </div>
      )}

      {/* Event Monitor Settings modal (create / edit) */}
      <EventMonitorSettingsModal
        open={settingsOpen}
        onClose={() => { setSettingsOpen(false); setEditingAlarm(null) }}
        onSaved={() => onChanged?.()}
        projectId={projectId}
        sites={sites}
        editingAlarm={editingAlarm}
      />
    </div>
  )
}
